import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Chip,
  Box,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Edit,
  Delete,
  Settings,
  CheckCircle,
  Cancel,
  Description as DescriptionIcon
} from '@mui/icons-material';

const GlossaryCard = ({ glossary, onManage, onEdit, onDelete }) => {
  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        p: 3,
        flexGrow: 1
      }}>
        {/* Header with title and status chip */}
        <Box 
          display="flex" 
          alignItems="flex-start" 
          justifyContent="space-between" 
          mb={2}
        >
          <Typography 
            variant="h6" 
            component="div"
            sx={{ 
              position: 'relative',
              fontWeight: 600,
              pr: 1.5,
              flexGrow: 1,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: '56px',
              '&::after': {
                content: '" |"',
                position: 'absolute',
                right: 0,
                bottom: 0,
                color: 'text.disabled',
                fontWeight: 700,
                lineHeight: 1,
              },
            }}
          >
            {glossary.name}
          </Typography>
          {glossary.is_active ? (
            <Chip 
              label="Active" 
              color="success" 
              size="small" 
              icon={<CheckCircle sx={{ fontSize: 16 }} />}
              sx={{ 
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                flexShrink: 0
              }}
            />
          ) : (
            <Chip 
              label="Inactive" 
              color="default" 
              size="small" 
              icon={<Cancel sx={{ fontSize: 16 }} />}
              sx={{ 
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                flexShrink: 0
              }}
            />
          )}
        </Box>
        
        {/* Description - Truncated to 2 lines */}
        <Box sx={{ mb: 2 }}>
          <Tooltip title={glossary.description || 'No description'} placement="bottom-start">
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                position: 'relative',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.5,
                pr: 1,
                '&::after': {
                  content: '" |"',
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  color: 'text.disabled',
                  fontWeight: 700,
                  lineHeight: 1.5,
                },
              }}
            >
              {glossary.description || (
                <Box component="span" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                  No description provided
                </Box>
              )}
            </Typography>
          </Tooltip>
        </Box>
        
        {/* Spacer to push content below to bottom */}
        <Box sx={{ flexGrow: 1 }} />
        
        <Divider sx={{ mb: 2, flexShrink: 0 }} />
        
        {/* Metadata */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1} 
          sx={{ flexShrink: 0 }}
        >
          <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {glossary.terms_count || 0} {glossary.terms_count === 1 ? 'term' : 'terms'}
          </Typography>
        </Box>
      </CardContent>
      
      {/* Actions */}
      <Divider sx={{ flexShrink: 0 }} />
      <CardActions sx={{ 
        p: 2, 
        pt: 1.5, 
        gap: 0.5, 
        flexShrink: 0
      }}>
        <Button 
          size="small" 
          onClick={onManage}
          startIcon={<Settings />}
          sx={{ fontWeight: 500 }}
        >
          Manage
        </Button>
        {onEdit && (
          <Button 
            size="small" 
            onClick={onEdit}
            startIcon={<Edit />}
            sx={{ fontWeight: 500 }}
          >
            Edit
          </Button>
        )}
        {onDelete && (
          <Button 
            size="small" 
            color="error" 
            onClick={onDelete}
            startIcon={<Delete />}
            sx={{ fontWeight: 500, ml: onEdit ? 0 : 'auto' }}
          >
            Delete
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default GlossaryCard;
